"use client";

/**
 * SmartButton
 * Intelligent button/link that detects form links and hotel-booking links,
 * opening them in a modal. Falls back to regular link behavior for external/anchor links.
 */

import { useEffect, useState } from "react";
import { parseFormLink, parseHotelBookingLink, isExternalLink } from "../../_logic/link-utils";
import type { FormStepLink } from "../../_logic/types";
import GenericModal from "@/components/ui/modal/GenericModal";
import FormModal from "./FormModal";

interface SmartButtonProps {
  href: string;
  text: string;
  primaryColor: string;
  eventId: number;
  className?: string;
  style?: React.CSSProperties;
  nextSteps?: FormStepLink[];
}

export default function SmartButton({
  href,
  text,
  primaryColor,
  eventId,
  className = "",
  style = {},
  nextSteps,
}: SmartButtonProps) {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [nextStepUrl, setNextStepUrl] = useState<string | null>(null);
  const [nextStepIdx, setNextStepIdx] = useState(0);
  const [allStepsComplete, setAllStepsComplete] = useState(false);

  // Parse the link to detect type
  const { isFormLink, formId } = parseFormLink(href, eventId);
  const { isHotelBookingLink } = parseHotelBookingLink(href);

  // Listen for hotel-booking-complete from the iframe
  useEffect(() => {
    if (!isBookingModalOpen) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "hotel-booking-complete") {
        if (nextStepUrl) {
          // A next-step iframe completed — check if more steps remain
          if (!nextSteps || nextStepIdx >= nextSteps.length) {
            setAllStepsComplete(true);
          } else {
            setBookingComplete(true);
          }
          setNextStepUrl(null);
        } else {
          // The initial booking completed
          setBookingComplete(true);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isBookingModalOpen, nextStepUrl, nextSteps, nextStepIdx]);

  const handleBookingClose = () => {
    setIsBookingModalOpen(false);
    setBookingComplete(false);
    setNextStepUrl(null);
    setNextStepIdx(0);
    setAllStepsComplete(false);
  };

  // Default button styling with primaryColor gradient (unless custom background provided)
  const defaultStyle: React.CSSProperties = {
    ...(style.backgroundColor || style.background
      ? {}
      : { background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)` }),
    ...style,
  };

  // Build the embedded URL for hotel booking
  const embeddedBookingUrl = isHotelBookingLink ? `${href}${href.includes("?") ? "&" : "?"}embedded=true` : "";

  // If it's a form link, render button that opens form modal
  if (isFormLink && formId) {
    return (
      <>
        <button
          onClick={() => setIsFormModalOpen(true)}
          className={`text-white rounded-lg font-semibold transition-all hover:opacity-90 ${
            className || "px-8 py-4 text-lg shadow-lg"
          }`}
          style={defaultStyle}
        >
          {text}
        </button>
        <FormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          formId={formId}
          eventId={eventId}
          primaryColor={primaryColor}
          nextSteps={nextSteps}
        />
      </>
    );
  }

  // If it's a hotel-booking link, render button that opens booking in a modal iframe
  if (isHotelBookingLink) {
    return (
      <>
        <button
          onClick={() => setIsBookingModalOpen(true)}
          className={`text-white rounded-lg font-semibold transition-all hover:opacity-90 ${
            className || "px-8 py-4 text-lg shadow-lg"
          }`}
          style={defaultStyle}
        >
          {text}
        </button>
        <GenericModal
          isOpen={isBookingModalOpen}
          onClose={handleBookingClose}
          title=""
          className="w-[95vw] max-w-[850px]"
        >
          {/* Active iframe — booking or next step */}
          {(!bookingComplete || nextStepUrl) && !allStepsComplete && (
            <div className="-mx-6 -mb-4">
              <iframe
                src={nextStepUrl || embeddedBookingUrl}
                className="w-full border-0 rounded-b-lg"
                style={{ minHeight: "80vh" }}
                title={nextStepUrl ? "Next Step" : "Hotel Booking"}
              />
            </div>
          )}

          {/* Booking complete — show next steps or done */}
          {bookingComplete && !nextStepUrl && !allStepsComplete && (
            <div className="text-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <svg
                  className="w-8 h-8"
                  style={{ color: primaryColor }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking submitted!</h3>
              {nextSteps && nextStepIdx < nextSteps.length ? (
                <>
                  <p className="text-gray-600 mb-6">Continue to the next step below.</p>
                  <div className="space-y-3">
                    <button
                      className="block w-full px-8 py-3 text-white rounded-lg font-semibold transition-all hover:opacity-90"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                      }}
                      onClick={() => {
                        const step = nextSteps[nextStepIdx];
                        const sep = step.url.includes("?") ? "&" : "?";
                        setNextStepUrl(`${step.url}${sep}embedded=true`);
                        setBookingComplete(false);
                        setNextStepIdx((prev) => prev + 1);
                      }}
                    >
                      {nextSteps[nextStepIdx].label || "Continue"}
                    </button>
                    <button
                      onClick={handleBookingClose}
                      className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Skip for now
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">Your hotel reservation has been submitted successfully.</p>
                  <button
                    onClick={handleBookingClose}
                    className="px-8 py-3 text-white rounded-lg font-semibold transition-all hover:opacity-90"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                    }}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          )}

          {/* All steps done */}
          {allStepsComplete && (
            <div className="text-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <svg
                  className="w-8 h-8"
                  style={{ color: primaryColor }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">All done!</h3>
              <p className="text-gray-600 mb-6">All steps completed successfully.</p>
              <button
                onClick={handleBookingClose}
                className="px-8 py-3 text-white rounded-lg font-semibold transition-all hover:opacity-90"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                }}
              >
                Close
              </button>
            </div>
          )}
        </GenericModal>
      </>
    );
  }

  // Otherwise, render regular link
  return (
    <a
      href={href}
      className={`inline-flex items-center text-white rounded-lg font-semibold transition-all hover:opacity-90 ${
        className || "px-8 py-4 text-lg shadow-lg"
      }`}
      style={defaultStyle}
      target={isExternalLink(href) ? "_blank" : undefined}
      rel={isExternalLink(href) ? "noopener noreferrer" : undefined}
    >
      {text}
    </a>
  );
}
